/**
 * ViewSwitcher Component
 * Dropdown to switch between CRM and Pipedrive views
 */

import { useNavigate, useLocation } from 'react-router-dom'
import { ChevronDown, Database } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { useUser } from '@/contexts/UserContext'

interface ViewOption {
  id: string
  label: string
  path: string
  color: string
}

const CRM_VIEW: ViewOption = { id: 'crm', label: 'Grackle CRM', path: '/', color: 'bg-primary' }
const PIPEDRIVE_VIEW: ViewOption = {
  id: 'pipedrive',
  label: 'Pipedrive Data',
  path: '/pipedrive',
  color: 'bg-[#FFC857]', // Pollen — on-brand workspace dot (was off-brand orange)
}
const HR_VIEW: ViewOption = { id: 'hr', label: 'HR', path: '/hr', color: 'bg-[#941052]' }
const TRAINING_VIEW: ViewOption = {
  id: 'training',
  label: 'Training (Beta)',
  path: '/training',
  color: 'bg-[#2A5D62]',
}

export function ViewSwitcher() {
  const navigate = useNavigate()
  const location = useLocation()
  const { userType } = useUser()

  // Accounts with no CRM access see only their own workspace: 'Employee' gets HR,
  // 'Training' gets the training worklist. Everyone else can switch freely.
  const hrOnly = userType === 'Employee'
  const trainingOnly = userType === 'Training'
  const viewOptions: ViewOption[] = hrOnly
    ? [HR_VIEW]
    : trainingOnly
      ? [TRAINING_VIEW]
      : [CRM_VIEW, PIPEDRIVE_VIEW, HR_VIEW, TRAINING_VIEW]

  const isHr = location.pathname.startsWith('/hr')
  const isPipedrive = location.pathname.startsWith('/pipedrive')
  const isTraining = location.pathname.startsWith('/training')
  const currentView = isTraining
    ? TRAINING_VIEW
    : isHr
      ? HR_VIEW
      : isPipedrive
        ? PIPEDRIVE_VIEW
        : viewOptions[0]

  if (!currentView) return null

  const handleViewChange = (view: ViewOption) => {
    navigate(view.path)
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="gap-2 px-3"
          aria-label={`Current view: ${currentView.label}. Click to switch views.`}
        >
          <div className={cn('h-2 w-2 rounded-full', currentView.color)} aria-hidden="true" />
          <span className="font-medium">{currentView.label}</span>
          <ChevronDown className="h-4 w-4 opacity-50" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-48">
        {viewOptions.map((view) => (
          <DropdownMenuItem
            key={view.id}
            onClick={() => handleViewChange(view)}
            className={cn(
              'flex items-center gap-2 cursor-pointer',
              // Pair the Navy accent bg with its white foreground so the selected
              // row is readable even when not hovered/focused.
              currentView.id === view.id && 'bg-accent text-accent-foreground'
            )}
            aria-current={currentView.id === view.id ? 'page' : undefined}
          >
            <div className={cn('h-2 w-2 rounded-full', view.color)} aria-hidden="true" />
            <Database className="h-4 w-4" aria-hidden="true" />
            <span>{view.label}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
